<template>
	<div v-if="mounted">
		<div class="sections">
			<section v-if="withSchoolTypeSelection" class="school-type-container">
				<h3>Art der Ausbildung</h3>
				<div v-if="schoolType">
					<Label :characteristic="schoolType[0].item" label-type="application"/>
					<a href="#" @click.prevent="schoolType = undefined">
						<Icon icon="edit" color="primary"/>
					</a>
				</div>
				<SelectInline
					v-else
					v-model="schoolType"
					:characteristic-items="meta.schoolType"
					initial-show-all
					single-choice
					label-type="application"
					:description-config="{ inline: true, html: true }"
					@input="schoolTypeChanged"
				/>
			</section>
			<template v-if="schoolType">
				<section v-if="expertProfessionEligible">
					<h3>{{ getDynamicLabel('expertProfessionTitle') }}</h3>
					<SelectInline
						v-model="expertProfession"
						:characteristic-items="meta.educationExpertProfession"
						single-choice
						label-type="application"
						@input="emitChangeEvent"
					/>
				</section>
				<section v-if="appraiserAuthorityEligible">
					<h3>Ist damit eine offizielle Befugnis verbunden, als Sachverständige/r tätig zu werden?</h3>
					<SelectInline
						v-model="appraiserAuthority"
						:characteristic-items="meta.educationAppraiserAuthority"
						:nothing-allowed="{ text: 'Keine' }"
						show-multiple-hint
						label-type="application"
						@input="emitChangeEvent"
					/>
				</section>
				<section v-if="workPermissionEligible">
					<h3>Bitte wähle Deine Berufsberechtigung</h3>
					<SelectInline
						v-model="workPermission"
						:characteristic-items="meta.educationWorkPermission"
						single-choice
						label-type="application"
						@input="emitChangeEvent"
					/>
				</section>
				<section v-if="titleEligible && titleRequired">
					<h3>{{ getDynamicLabel('titleTitle') }}</h3>
					<b-input
						v-model="title"
						type="text"
						:placeholder="getDynamicLabel('titlePlaceholder')"
						@input="emitChangeEvent"
					/>
				</section>
				<section v-if="instituteNameRequired">
					<h3>{{ getDynamicLabel('instituteNameTitle') }}</h3>
					<b-input v-model="instituteName" type="text" @input="emitChangeEvent"/>
				</section>
				<section v-if="furtherInstitutionTypeEligible">
					<h3>Art des Ausbildungsinstitutes</h3>
					<FurtherInstitutionTypeSingleSelect
						v-model="furtherInstitutionType"
						:characteristics="meta.educationFurtherInstitutionType"
						undefined-text-option-text="--- Bitte triff eine Auswahl ---"
						label-type="application"
						@input="emitChangeEvent"
					/>
				</section>
				<section v-if="detailsOnTop">
					<h3>
						{{ getDynamicLabel('detailsTitle') }}
						<span v-if="getDynamicLabel('detailsTooltip')" class="d-block font-weight-normal mt-1">
							{{ getDynamicLabel('detailsTooltip') }}
						</span>
					</h3>
					<Wysiwyg v-model="details" @input="emitChangeEvent"/>
				</section>
				<section v-if="professionEligible">
					<h3>{{ getDynamicLabel('professionTitle') }}</h3>
					<div v-if="!professionsValid" class="error">
						{{ getDynamicLabel('professionError') }}
					</div>
					<CharacteristicEdit
						v-model="professions"
						ref="professions"
						:title="getDynamicLabel('professionTitleFan') || getDynamicLabel('professionTitle')"
						:with-keyword="true"
						:characteristic-type="meta.schoolProfessionType"
						:characteristic-items="schoolProfessionMetaItems"
						show-multiple-hint
						open-fan-button-alignment="left"
						open-fan-label="Fachliche/n Schwerpunkt/e auswählen"
						open-fan-label-with-selection="Fachliche/n Schwerpunkt/e bearbeiten"
						label-type="application"
						class="mt-3"
						@input="emitChangeEvent"
					/>
				</section>
				<section v-if="educationCompletionEligible">
					<h3>{{ getDynamicLabel('educationCompletionTitle') }}</h3>
					<SelectInline
						v-model="educationCompletion"
						:characteristic-items="meta.educationCompletion"
						single-choice
						label-type="application"
						@input="emitChangeEvent"
					/>
				</section>
				<section v-if="educationCompletionTypeEligible">
					<h3>Abschluss der Ausbildung durch</h3>
					<EducationCompletionTypeSingleSelect
						v-model="educationCompletionType"
						:characteristics="meta.educationCompletionTypeItems"
						undefined-text-option-text="--- Bitte triff eine Auswahl ---"
						label-type="application"
						@input="emitChangeEvent"
					/>
				</section>
				<section v-if="gainedTitleTypeEligible">
					<h3>{{ getDynamicLabel('gainedTitleTitle') }}</h3>
					<div class="d-flex align-items-center">
						<div>
							<Select
								v-model="gainedTitleType"
								:options="gainedTitleTypeOptions"
								@input="emitChangeEvent"
							/>
						</div>
						<div v-if="gainedTitleEligible" class="ml-2">
							<b-input
								v-model="gainedTitle"
								type="text"
								:placeholder="gainedTitleLabels[gainedTitleType]"
								@input="emitChangeEvent"
							/>
						</div>
					</div>
				</section>
				<section v-if="scopeCountriesEligible">
					<h3>Geltungsbereich</h3>
					<ScopeCountryMultiSelect
						v-model="scopeCountries"
						:meta="meta"
						:initial-nothing="scopeCountriesInitialWorldwide"
						nothing-eligible
						nothing-text="Unbeschränkt"
						@input="emitChangeEvent"
					/>
				</section>
				<section v-if="studyEntitlementEligible">
					<h3>{{ getDynamicLabel('studyEntitlementTitle') }}</h3>
					<RadioGroup
						v-model="studyEntitlement"
						:options="yesNoOptions"
						@input="emitChangeEvent"
					/>
				</section>
				<section v-if="postgraduateEligible">
					<h3>Ist das eine postgraduale Ausbildung, wie MBA, LL.M., PhD oder Doktorat?</h3>
					<RadioGroup
						v-model="postgraduate"
						:options="yesNoOptions"
						@input="emitChangeEvent"
					/>
				</section>
				<section v-if="validityEligible">
					<h3>Gültigkeitsdauer - wie lange gilt die Ausbildung?</h3>
					<ValidityDateSelect
						v-model="validityDate"
						@input="emitChangeEvent"
					/>
				</section>
			</template>
		</div>
		<div v-if="withDetails && schoolType" class="mt-5">
			<h3>Details zur Ausbildung</h3>
			<div class="sections mt-4">
				<section>
					<h3>{{ getDynamicLabel('startEndTitle') }}</h3>
					<DateEdit
						v-model="startEnd"
						:show-required-error="false"
						@input="emitChangeEvent"
					/>
				</section>
				<section v-if="titleEligible && !titleRequired">
					<h3>{{ getDynamicLabel('titleTitle') }}</h3>
					<b-input
						v-model="title"
						type="text"
						:placeholder="getDynamicLabel('titlePlaceholder')"
						@input="emitChangeEvent"
					/>
				</section>
				<section v-if="!instituteNameRequired">
					<h3>{{ getDynamicLabel('instituteNameTitle') }}</h3>
					<b-input v-model="instituteName" type="text" @input="emitChangeEvent"/>
				</section>
				<section v-if="!detailsOnTop">
					<h3>
						{{ getDynamicLabel('detailsTitle') }}
						<span v-if="getDynamicLabel('detailsTooltip')" class="d-block font-weight-normal mt-1">
							{{ getDynamicLabel('detailsTooltip') }}
						</span>
					</h3>
					<Wysiwyg v-model="details" @input="emitChangeEvent"/>
				</section>
				<section v-if="schoolLocationEligible">
					<h3>{{ getDynamicLabel('schoolLocationTitle') }}</h3>
					<CharacteristicEdit
						v-model="schoolLocation"
						:title="getDynamicLabel('schoolLocationTitle')"
						:sub-title="getDynamicLabel('schoolLocationSubTitle')"
						:with-keyword="true"
						:characteristic-type="meta.geoType"
						:characteristic-items="schoolLocationMetaItems"
						open-fan-label="Ort/e auswählen"
						open-fan-label-with-selection="Ort/e bearbeiten"
						open-fan-button-alignment="left"
						label-type="application"
						@input="emitChangeEvent"
					/>
				</section>
			</div>
		</div>
	</div>
</template>

<script>
import ValidityDateSelect from "@/vue/job-profile/education/ValidityDateSelect";
import Select from "@/vue/common/form/Select";
import PopUpActions from "@/vue/common/popup/PopUpActions";
import FormGroup from "@/vue/common/form/Group";
import PopUp from "@/vue/common/PopUp";
import SchoolTypeSingleSelect from "@/vue/characteristic/SingleSelect";
import EducationCompletionSingleSelect from "@/vue/characteristic/SingleSelect";
import EducationCompletionTypeSingleSelect from "@/vue/characteristic/SingleSelect";
import FurtherInstitutionTypeSingleSelect from "@/vue/characteristic/SingleSelect";
import ScopeCountryMultiSelect from "@/vue/common/form/CountryMultiSelect";
import {isDefined, valueIfNotUndefined} from "@/helper/Value";
import {handle} from "@/helper/characteristic/Level";
import * as CharacteristicTag from "@/helper/characteristic/Tag";
import {isEmptyOrNull, stringOrNull} from "@/helper/String";
import {uuid} from "vue-uuid";
import {arrayIsNotEmpty} from "@/helper/Array";
import _ from "lodash";
import CharacteristicEdit from "@/vue/characteristic/Edit"
import Button from "@/vue/common/Button";
import RadioGroup from "@/vue/common/form/RadioGroup";
import IconLink from "@/vue/common/IconLink";
import DateEdit from "@/vue/application/career/DateEdit";
import SelectInline from "@/vue/characteristic/SelectInline.vue";
import {characteristicMultiple} from "@/helper/application/Populate";
import Icon from "@/vue/icon/Icon.vue";
import {
	SCHOOL_TYPE__APPRAISER,
	SCHOOL_TYPE__AUDITOR,
	SCHOOL_TYPE__COLLEGE,
	SCHOOL_TYPE__ELEMENTARY_SCHOOL,
	SCHOOL_TYPE__FURTHER_EDUCATION,
	SCHOOL_TYPE__HIGH_SCHOOL, SCHOOL_TYPE__WORK_PERMISSION
} from "@/helper/characteristic/NaturalId";
import Wysiwyg from "@/vue/common/form/Wysiwyg.vue";
import * as NaturalId from "@/helper/characteristic/NaturalId";
import {COMPLETED} from "@/helper/application/characteristic/EducationCompletion";
import Label from "@/vue/characteristic/Label.vue";
import {cleanUp, removeItemsWithCorrelation, TYPE__SUPPRESS_FOR_SCHOOL_TYPE} from "@/helper/characteristic/Correlation";

const GAINED_TITLE_PRE = 'pre';
const GAINED_TITLE_POST = 'post';

const DEFAULT_LABEL = 'default';

const DYNAMIC_LABELS = {
	professionTitle: {
		[SCHOOL_TYPE__COLLEGE]: 'Fachliche/r Schwerpunkt/e dieser Hochschulausbildung',
		[SCHOOL_TYPE__HIGH_SCHOOL]: 'Fachliche/r Schwerpunkt/e dieser Schulausbildung',
		[SCHOOL_TYPE__FURTHER_EDUCATION]: 'Fachliche/r Schwerpunkt/e dieser Weiterbildung',
		[DEFAULT_LABEL]: 'Fachliche/r Schwerpunkt/e der Ausbildung'
	},
	professionTitleFan: {
		[SCHOOL_TYPE__HIGH_SCHOOL]: 'Fachliche/r Schwerpunkt/e dieser Schulausbildung<br/>Beispiel: Warst Du auf einer HASCH oder HAK, dann wähle Wirtschaft.',
	},
	expertProfessionTitle: {
		[SCHOOL_TYPE__AUDITOR]: 'Fachgebiet als Auditor/in',
		[SCHOOL_TYPE__APPRAISER]: 'Fachgebiet als Sachverständige/r',
		[DEFAULT_LABEL]: undefined
	},
	professionError: {
		[SCHOOL_TYPE__COLLEGE]: 'Bitte gib zumindest einen fachlichen Schwerpunkt dieser Hochschulausbildung an.',
		[SCHOOL_TYPE__HIGH_SCHOOL]: 'Bitte gib zumindest einen fachlichen Schwerpunkt dieser Schulausbildung an.',
		[SCHOOL_TYPE__FURTHER_EDUCATION]: 'Bitte gib zumindest einen fachlichen Schwerpunkt dieser Weiterbildung an.',
		[DEFAULT_LABEL]: 'Bitte gib zumindest einen fachlichen Schwerpunkt dieser Ausbildung an.'
	},
	educationCompletionTitle: {
		[SCHOOL_TYPE__COLLEGE]: 'Hast Du diese Hochschulausbildung abgeschlossen?',
		[SCHOOL_TYPE__HIGH_SCHOOL]: 'Hast Du diese Schulausbildung abgeschlossen?',
		[DEFAULT_LABEL]: 'Hast Du diese Ausbildung abgeschlossen?'
	},
	gainedTitleTitle: {
		[SCHOOL_TYPE__COLLEGE]: 'Erwirbt man einen akademischen Grad mit dieser Hochschulausbildung?',
		[SCHOOL_TYPE__WORK_PERMISSION]: 'Erwirbt man einen akademischen Grad mit dieser Berufsberechtigung?',
		[DEFAULT_LABEL]: 'Erwirbt man einen akademischen Grad mit dieser Ausbildung?'
	},
	startEndTitle: {
		[SCHOOL_TYPE__COLLEGE]: 'Zeitraum: von wann bis wann dauerte die Hochschulausbildung',
		[SCHOOL_TYPE__HIGH_SCHOOL]: 'Zeitraum: von wann bis wann dauerte die Schulausbildung',
		[SCHOOL_TYPE__FURTHER_EDUCATION]: 'Zeitraum: von wann bis wann dauerte die Zusatzausbildung/Weiterbildung',
		[SCHOOL_TYPE__WORK_PERMISSION]: `Zeitraum: von wann bis wann dauerte die Ausbildung für diese
			Berufsberechtigung`,
		[SCHOOL_TYPE__AUDITOR]: `Zeitraum: von wann bis wann dauerte die Ausbildung für diese Auditorbefugnis`,
		[SCHOOL_TYPE__APPRAISER]: `Zeitraum: von wann bis wann dauerte die Ausbildung für diese Sachverständigenbefugnis`,
		[DEFAULT_LABEL]: 'Zeitraum: von wann bis wann dauerte die Ausbildung?'
	},
	titleTitle: {
		[SCHOOL_TYPE__COLLEGE]: 'Bezeichnung der Studienrichtung/der Hochschulausbildung',
		[SCHOOL_TYPE__HIGH_SCHOOL]: 'Bezeichnung dieser Schulausbildung',
		[SCHOOL_TYPE__FURTHER_EDUCATION]: 'Bezeichnung der Zusatzausbildung/Weiterbildung',
		[SCHOOL_TYPE__WORK_PERMISSION]: 'Bezeichnung der Berufsberechtigung',
		[SCHOOL_TYPE__AUDITOR]: 'Fachgebiet: genaue Bezeichnung der Auditor-Befugnis',
		[SCHOOL_TYPE__APPRAISER]: 'Fachgebiet: genaue Bezeichnung der Sachverständigen-Befugnis',
		[DEFAULT_LABEL]: 'Bezeichnung der Ausbildung'
	},
	titlePlaceholder: {
		[SCHOOL_TYPE__COLLEGE]: 'Zum Beispiel: Rechtwissenschaften, Humanmedizin, Pharmakologie, Anlagenbau, ...',
		[SCHOOL_TYPE__HIGH_SCHOOL]: 'Zum Beispiel: Gymnasium, HAK, AHS, Integrierte Gesamtschule, Kolleg, HASCH, Fachschule, Berufsschule, BHS',
		[DEFAULT_LABEL]: undefined
	},
	instituteNameTitle: {
		[SCHOOL_TYPE__COLLEGE]: 'Name der Hochschule',
		[SCHOOL_TYPE__HIGH_SCHOOL]: 'Name der Schule',
		[SCHOOL_TYPE__ELEMENTARY_SCHOOL]: 'Name der Grundschule',
		[SCHOOL_TYPE__FURTHER_EDUCATION]: 'Name des Anbieters dieser Zusatzausbildung/Weiterbildung (optional)',
		[SCHOOL_TYPE__WORK_PERMISSION]: 'Name des Ausbildungsinstitutes/der berechtigenden Stelle',
		[SCHOOL_TYPE__AUDITOR]: 'Name des Ausbildungsinstitutes/der berechtigenden Stelle',
		[SCHOOL_TYPE__APPRAISER]: 'Name des Ausbildungsinstitutes/der berechtigenden Stelle',
		[DEFAULT_LABEL]: 'Name der Bildungseinrichtung'
	},
	studyEntitlementTitle: {
		[SCHOOL_TYPE__HIGH_SCHOOL]: `Kann man durch den Abschluss dieser Schule danach eine Hochschule besuchen,
			wie eine Universität oder Fachhochschule?`,
		[SCHOOL_TYPE__FURTHER_EDUCATION]: `Kann man durch den Abschluss dieser Zusatzausbildung/Weiterbildung danach
			eine Hochschule besuchen, wie eine Universität oder Fachhochschule?`,
		[DEFAULT_LABEL]: undefined
	},
	detailsTitle: {
		[SCHOOL_TYPE__COLLEGE]: 'Details zur Hochschulausbildung (optional)',
		[SCHOOL_TYPE__HIGH_SCHOOL]: 'Details zur Schulausbildung (optional)',
		[SCHOOL_TYPE__FURTHER_EDUCATION]: 'Details zur Zusatzausbildung/Weiterbildung (optional)',
		[DEFAULT_LABEL]: 'Details (optional)'
	},
	detailsTooltip: {
		[SCHOOL_TYPE__COLLEGE]: 'Schwerpunkte, Thema der wissenschaftlichen Arbeit, Auslandsaufenthalte, weitere Details',
		[DEFAULT_LABEL]: undefined
	},
	schoolLocationTitle: {
		[SCHOOL_TYPE__COLLEGE]: 'Ort/e der Hochschulausbildung (optional)',
		[SCHOOL_TYPE__HIGH_SCHOOL]: 'Ort/e der Schulausbildung (optional)',
		[SCHOOL_TYPE__FURTHER_EDUCATION]: 'Ort/e der Zusatzausbildung/Weiterbildung (optional)',
		[DEFAULT_LABEL]: 'Ort/e der Ausbildung (optional)'
	},
	schoolLocationSubTitle: {
		[SCHOOL_TYPE__COLLEGE]: 'Mehrfachauswahl möglich, wenn du an mehreren Orten dieses Hochschulstudium besucht hast.',
		[SCHOOL_TYPE__HIGH_SCHOOL]: 'Mehrfachauswahl möglich, wenn du an mehreren Orten diese Schulausbildung gemacht hast.',
		[SCHOOL_TYPE__FURTHER_EDUCATION]: 'Mehrfachauswahl möglich, wenn du an mehreren Orten diese Zusatzausbildung/Weiterbildung gemacht hast.',
		[DEFAULT_LABEL]: undefined
	}
};

export default {

	components: {
		Label,
		Wysiwyg,
		Icon,
		SelectInline,
		DateEdit,
		IconLink,
		RadioGroup,
		Button,
		ValidityDateSelect,
		Select,
		PopUpActions,
		FormGroup,
		PopUp,
		CharacteristicEdit,
		SchoolTypeSingleSelect,
		EducationCompletionSingleSelect,
		EducationCompletionTypeSingleSelect,
		ScopeCountryMultiSelect,
		FurtherInstitutionTypeSingleSelect,
	},

	props: {
		value: {
			type: Object,
			required: false,
		},
		meta: {
			type: Object,
			required: true,
		},
		withSchoolTypeSelection: {
			type: Boolean,
			required: false,
			default: true
		},
		withDetails: {
			type: Boolean,
			required: false,
			default: true,
		}
	},

	mounted ()
	{
		if (this.professions)
		{
			this.professions = characteristicMultiple(
				this.professions,
				this.meta,
				this.meta.schoolProfession,
				this.meta.schoolProfessionType,
				true
			);
		}

		if (this.schoolLocation)
		{
			this.schoolLocation = characteristicMultiple(
				this.schoolLocation,
				this.meta,
				this.meta.geo,
				this.meta.geoType,
				true
			);
		}

		this.emitChangeEvent();

		this.mounted = true;
	},

	data ()
	{
		const isAddition = !isDefined(this.value);

		let startEnd = undefined;

		if (!isAddition)
		{
			if (this.value?.start || this.value?.end)
			{
				startEnd = {
					start: this.value?.start,
					end: this.value?.end,
				};
			}
		}

		const validityDate = valueIfNotUndefined(this.value?.validityDate);

		return {
			mounted: false,
			id: isAddition ? uuid.v4() : this.value.id,
			startEnd,
			isAddition,
			schoolType: this.value?.schoolType !== undefined
				? [{item: this.value.schoolType}]
				: undefined,
			schoolLocation: this.value?.schoolLocation,
			professions: this.value?.professions,
			educationCompletion: this.value?.educationCompletion !== undefined
				? [{item: this.value.educationCompletion}]
				: undefined,
			workPermission: this.value?.workPermission !== undefined
				? [{item: this.value.workPermission}]
				: undefined,
			expertProfession: this.value?.expertProfession !== undefined
				? [{item: this.value.expertProfession}]
				: undefined,
			appraiserAuthority: this.value?.appraiserAuthority,
			educationCompletionType: valueIfNotUndefined(this.value?.educationCompletionType) || undefined,
			gainedTitleType: valueIfNotUndefined(this.value?.gainedTitleType),
			gainedTitle: valueIfNotUndefined(this.value?.gainedTitle) || '',
			postgraduate: this.value?.postgraduate,
			studyEntitlement: this.value?.studyEntitlement,
			scopeCountries: valueIfNotUndefined(this.value?.scopeCountries),
			validityDate,
			instituteName: valueIfNotUndefined(this.value?.instituteName) || '',
			furtherInstitutionType: valueIfNotUndefined(this.value?.furtherInstitutionType) || undefined,
			title: valueIfNotUndefined(this.value?.title) || '',
			details: valueIfNotUndefined(this.value?.details) || '',
			schoolLocationMetaItems: handle(this.meta.geo, 1)
		};
	},

	computed: {
		schoolProfessionMetaItems ()
		{
			const allItems = this.maxCharacteristicLevel(
				_.cloneDeep(this.meta.schoolProfession),
				2
			);

			const schoolTypeCharacteristic = this.schoolType && arrayIsNotEmpty(this.schoolType)
				? _.first(this.schoolType).item
				: undefined;

			if (!schoolTypeCharacteristic)
			{
				return allItems;
			}

			return removeItemsWithCorrelation({
				items: allItems,
				target: schoolTypeCharacteristic,
				type: TYPE__SUPPRESS_FOR_SCHOOL_TYPE
			});
		},
		yesNoOptions ()
		{
			return [
				{value: true, text: 'Ja'},
				{value: false, text: 'Nein'},
			];
		},
		postgraduateEligible ()
		{
			return this.schoolTypeIsCollege;
		},
		postgraduateValid ()
		{
			return !this.postgraduateEligible || isDefined(this.postgraduate);
		},
		studyEntitlementEligible ()
		{
			return this.schoolTypeIsHighSchool || this.schoolTypeIsFurtherEducation;
		},
		studyEntitlementValid ()
		{
			return !this.studyEntitlementEligible || isDefined(this.studyEntitlement);
		},
		schoolTypeItem ()
		{
			return this.schoolType ? _.first(this.schoolType).item : undefined;
		},
		schoolTypeIsElementarySchool ()
		{
			return this.schoolTypeItem?.naturalId === NaturalId.SCHOOL_TYPE__ELEMENTARY_SCHOOL;
		},
		schoolTypeIsHighSchool ()
		{
			return this.schoolTypeItem?.naturalId === NaturalId.SCHOOL_TYPE__HIGH_SCHOOL;
		},
		schoolTypeIsCollege ()
		{
			return this.schoolTypeItem?.naturalId === NaturalId.SCHOOL_TYPE__COLLEGE;
		},
		schoolTypeIsFurtherEducation ()
		{
			return this.schoolTypeItem?.naturalId === NaturalId.SCHOOL_TYPE__FURTHER_EDUCATION;
		},
		schoolTypeIsWorkPermission ()
		{
			return this.schoolTypeItem?.naturalId === NaturalId.SCHOOL_TYPE__WORK_PERMISSION;
		},
		schoolTypeIsAuditor ()
		{
			return this.schoolTypeItem?.naturalId === NaturalId.SCHOOL_TYPE__AUDITOR;
		},
		schoolTypeIsAppraiser ()
		{
			return this.schoolTypeItem?.naturalId === NaturalId.SCHOOL_TYPE__APPRAISER;
		},
		workPermissionEligible ()
		{
			return this.schoolTypeIsWorkPermission;
		},
		workPermissionValid ()
		{
			return !this.workPermissionEligible || isDefined(this.workPermission);
		},
		expertProfessionEligible ()
		{
			return this.schoolTypeIsAuditor || this.schoolTypeIsAppraiser;
		},
		expertProfessionValid ()
		{
			return !this.expertProfessionEligible || isDefined(this.expertProfession);
		},
		appraiserAuthorityEligible ()
		{
			return this.schoolTypeIsAppraiser;
		},
		appraiserAuthorityValid ()
		{
			return !this.appraiserAuthorityEligible || isDefined(this.appraiserAuthority);
		},
		professionEligible ()
		{
			return this.schoolTypeItem
				&& CharacteristicTag.hasTag(this.schoolTypeItem, CharacteristicTag.CAREER_EDUCATION_PROFESSION_ELIGIBLE);
		},
		professionsValid ()
		{
			return !this.professionEligible || isDefined(this.professions);
		},
		forcedEducationCompletion ()
		{
			const force = this.schoolTypeIsWorkPermission
				|| this.schoolTypeIsAuditor
				|| this.schoolTypeIsAppraiser;

			return force
				? _.find(this.meta.educationCompletion, characteristic => characteristic.naturalId === COMPLETED)
				: undefined;
		},
		educationCompletionEligible ()
		{
			return isDefined(this.schoolTypeItem)
				&& !this.forcedEducationCompletion;
		},
		educationCompletionCharacteristic ()
		{
			if (this.forcedEducationCompletion)
			{
				return this.forcedEducationCompletion;
			}

			return isDefined(this.educationCompletion)
				? _.first(this.educationCompletion).item
				: undefined;
		},
		educationCompletionValid ()
		{
			return isDefined(this.forcedEducationCompletion) || isDefined(this.educationCompletion);
		},
		educationCompletionTypeEligible ()
		{
			return this.schoolTypeIsFurtherEducation;
		},
		educationCompletionTypeValid ()
		{
			return !this.educationCompletionTypeEligible || isDefined(this.educationCompletionType);
		},
		furtherInstitutionTypeEligible ()
		{
			return this.schoolTypeIsFurtherEducation;
		},
		furtherInstitutionTypeValid ()
		{
			return !this.furtherInstitutionTypeEligible || isDefined(this.furtherInstitutionType);
		},
		isEducationCompleted ()
		{
			return this.educationCompletionCharacteristic
				&& CharacteristicTag.hasTag(
					this.educationCompletionCharacteristic,
					CharacteristicTag.CAREER_EDUCATION_COMPLETED
				);
		},
		gainedTitleTypeEligible ()
		{
			return this.schoolTypeIsCollege || this.schoolTypeIsWorkPermission;
		},
		gainedTitleTypeOptions ()
		{
			return [
				{value: null, text: 'nein - das ist keine akademische Ausbildung'},
				{value: GAINED_TITLE_PRE, text: 'ja - Titel vor dem Namen'},
				{value: GAINED_TITLE_POST, text: 'ja - Titel nach dem Namen'},
			];
		},
		gainedTitleEligible ()
		{
			return isDefined(this.gainedTitleType);
		},
		gainedTitleLabels ()
		{
			return {
				[GAINED_TITLE_PRE]: 'Titel vor dem Namen',
				[GAINED_TITLE_POST]: 'Titel nach dem Namen',
			};
		},
		gainedTitleValid ()
		{
			if (!this.gainedTitleTypeEligible)
			{
				return true;
			}

			return this.gainedTitleType !== undefined
				&& (!this.gainedTitleEligible || !isEmptyOrNull(this.gainedTitle));
		},
		scopeCountriesEligible ()
		{
			return this.schoolTypeItem && CharacteristicTag.hasTag(
				this.schoolTypeItem,
				CharacteristicTag.CAREER_EDUCATION_SCOPE_COUNTRIES_ELIGIBLE
			);
		},
		scopeCountriesValid ()
		{
			return !this.scopeCountriesEligible || isDefined(this.scopeCountries);
		},
		scopeCountriesInitialWorldwide ()
		{
			return isDefined(this.scopeCountries) && this.scopeCountries.length === 0;
		},
		validityEligible ()
		{
			return this.isEducationCompleted
				&& this.schoolTypeItem
				&& CharacteristicTag.hasTag(this.schoolTypeItem, CharacteristicTag.CAREER_EDUCATION_VALIDITY_ELIGIBLE);
		},
		validityValid ()
		{
			return !this.validityEligible || this.validityDate !== undefined;
		},
		instituteNameRequired ()
		{
			return this.schoolTypeIsElementarySchool;
		},
		instituteNameValid ()
		{
			return !this.instituteNameRequired || !isEmptyOrNull(this.instituteName);
		},
		titleEligible ()
		{
			return !this.schoolTypeIsElementarySchool;
		},
		titleRequired ()
		{
			return this.schoolTypeIsWorkPermission || this.schoolTypeIsFurtherEducation;
		},
		titleValid ()
		{
			if (!this.titleEligible)
			{
				return true;
			}

			return !this.titleRequired || !isEmptyOrNull(this.title);
		},
		detailsOnTop ()
		{
			return this.schoolTypeIsElementarySchool;
		},
		schoolLocationEligible ()
		{
			return !this.schoolTypeIsWorkPermission
				&& !this.schoolTypeIsAuditor
				&& !this.schoolTypeIsAppraiser;
		},
		valid ()
		{
			return isDefined(this.schoolType)
				&& this.instituteNameValid
				&& this.furtherInstitutionTypeValid
				&& this.titleValid
				&& this.gainedTitleValid
				&& this.educationCompletionValid
				&& this.educationCompletionTypeValid
				&& this.professionsValid
				&& this.validityValid
				&& this.postgraduateValid
				&& this.studyEntitlementValid
				&& this.workPermissionValid
				&& this.scopeCountriesValid
				&& this.expertProfessionValid
				&& this.appraiserAuthorityValid;
		},
	},

	methods: {
		schoolTypeChanged()
		{
			this.professions = cleanUp({
				selected: this.professions,
				characteristicItems: this.schoolProfessionMetaItems
			});

			this.emitChangeEvent();
		},
		getDynamicLabel (key)
		{
			return DYNAMIC_LABELS[key][this.schoolTypeItem?.naturalId] || DYNAMIC_LABELS[key][DEFAULT_LABEL];
		},
		maxCharacteristicLevel (characteristics)
		{
			return handle(characteristics, 1);
		},
		emitChangeEvent ()
		{
			if (!this.valid)
			{
				this.$emit('input', undefined);
				return;
			}

			const data = {
				id: this.id,
				type: 'education',
				start: this.startEnd?.start,
				end: this.startEnd?.end,
				schoolType: this.schoolTypeItem,
				professions: this.professionEligible
					? this.professions
					: [],
				educationCompletion: this.forcedEducationCompletion
					? this.forcedEducationCompletion
					: _.first(this.educationCompletion).item,
				postgraduate: this.postgraduateEligible
					? this.postgraduate
					: undefined,
				studyEntitlement: this.studyEntitlementEligible
					? this.studyEntitlement
					: undefined
			};

			if (this.isEducationCompleted)
			{
				data.validityDate = this.validityDate;
			}

			if (this.educationCompletionTypeEligible)
			{
				data.educationCompletionType = this.educationCompletionType;
			}

			if (this.furtherInstitutionTypeEligible)
			{
				data.furtherInstitutionType = this.furtherInstitutionType;
			}

			if (this.workPermissionEligible)
			{
				data.workPermission = _.first(this.workPermission).item;
			}

			if (this.expertProfessionEligible)
			{
				data.expertProfession = _.first(this.expertProfession).item;
			}

			if (this.appraiserAuthorityEligible)
			{
				data.appraiserAuthority = this.appraiserAuthority;
			}

			data.title = this.titleEligible && !isEmptyOrNull(this.title)
				? this.title
				: '';

			data.instituteName = stringOrNull(this.instituteName);

			if (this.schoolLocationEligible)
			{
				data.schoolLocation = arrayIsNotEmpty(this.schoolLocation)
					? this.schoolLocation
					: undefined;
			}

			if (this.gainedTitleTypeEligible)
			{
				data.gainedTitleType = this.gainedTitleType;
			}

			if (this.gainedTitleEligible)
			{
				data.gainedTitle = this.gainedTitle;
			}

			data.scopeCountries = this.scopeCountries;

			data.details = stringOrNull(this.details);

			this.$emit('input', data);
		},
	},
}
</script>
